import CloseIcon from '@mui/icons-material/Close';
import { skipToken } from '@reduxjs/toolkit/query';
import DOMPurify from 'dompurify';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { AppUserInfo } from '@/models/AppViewModel';
import type { ModuleModalProps } from '@/permissions';
import {
  ECONSENT_LOGIN_PROMPT_CHECKED_KEY,
  ECONSENT_LOGIN_PROMPT_ELIGIBLE_KEY,
  ECONSENT_LOGIN_PROMPT_VERIFICATION_FILENAME_KEY,
  ECONSENT_STATUS_CHANGED_EVENT,
  type EConsentStatusChangedDetail,
} from '@/constants/econsent';
import {
  resolveEConsentPromptTitle,
  resolveDefaultEConsentPromptWindowConfig,
  resolveRuntimeEConsentConfig,
} from '@/store/eConsentConfigApi';
import {
  useGetEConsentStatusMutation,
  useMarkEConsentUserIgnoredMutation,
  useSubmitEConsentMutation,
  type EConsentStatusRequest,
} from '@/store/eConsentApi';
import type { WARootState } from '@/store/store';
import { useGetUserProfileQuery } from '@/store/usersApi';
import { hasUserProfile } from '@/constants/roles';
import { deriveAdvisorGroupId } from '@/utils/deriveAdvisorGroupId';
import { isRecord } from '@/utils/apiPayload';
import {
  extractHashFromPdfFilename,
  fetchRandomVerificationPdf,
  verifyPdfCode,
} from '@/utils/verificationCodes';
import { triggerBlobDownload } from '@/utils/triggerBlobDownload';
import { MimeType } from '@/utils/fileValidation';
import { hasVisibleHtmlContent } from '@/utils/htmlContent';

interface EConsentAppUserInfo extends AppUserInfo {
  ExternalId?: string | undefined;
}

interface EConsentProfile {
  FirstName?: string;
  LastName?: string;
  EmailAddress?: string;
}

function getErrorMessage(error: unknown): string {
  if (!isRecord(error)) {
    return '';
  }

  if ('data' in error && isRecord(error.data)) {
    const data = error.data;
    const message = data.message;
    if (typeof message === 'string' && message.trim()) {
      return message;
    }
    const nestedError = data.error;
    if (typeof nestedError === 'string' && nestedError.trim()) {
      return nestedError;
    }
  }

  if ('message' in error && typeof error.message === 'string') {
    return error.message;
  }

  return '';
}

function getTrimmedExternalId(
  userInfo: EConsentAppUserInfo | undefined,
): string | null {
  const externalId = userInfo?.ExternalId?.trim();
  return externalId ? externalId : null;
}

function buildConsentStatusRequest(
  userInfo: EConsentAppUserInfo | undefined,
  advisorGroupId: number | null,
  profile: EConsentProfile | undefined,
): EConsentStatusRequest | null {
  const externalId = getTrimmedExternalId(userInfo);
  const firstName = profile?.FirstName?.trim();
  const lastName = profile?.LastName?.trim();
  const email = profile?.EmailAddress?.trim();

  if (
    !userInfo?.UserId ||
    advisorGroupId === null ||
    !firstName ||
    !lastName ||
    !email
  ) {
    return null;
  }

  return {
    user_id: userInfo.UserId,
    fi_id: advisorGroupId,
    external_id: externalId ?? '',
    first_name: firstName,
    last_name: lastName,
    email,
  };
}

function isNumericVerificationCode(value: string): boolean {
  return /^\d+$/.test(value.trim());
}

function isFourDigitVerificationCode(value: string): boolean {
  return /^\d{4}$/.test(value.trim());
}

function persistPendingPromptState(verificationFilename: string | null) {
  window.sessionStorage.setItem(ECONSENT_LOGIN_PROMPT_CHECKED_KEY, '1');
  window.sessionStorage.setItem(ECONSENT_LOGIN_PROMPT_ELIGIBLE_KEY, '1');

  if (verificationFilename) {
    window.sessionStorage.setItem(
      ECONSENT_LOGIN_PROMPT_VERIFICATION_FILENAME_KEY,
      verificationFilename,
    );
    return;
  }

  window.sessionStorage.removeItem(
    ECONSENT_LOGIN_PROMPT_VERIFICATION_FILENAME_KEY,
  );
}

function persistAnsweredPromptState(
  detail: Omit<EConsentStatusChangedDetail, 'submitted'> = {},
) {
  window.sessionStorage.setItem(ECONSENT_LOGIN_PROMPT_CHECKED_KEY, '1');
  window.sessionStorage.setItem(ECONSENT_LOGIN_PROMPT_ELIGIBLE_KEY, '0');
  window.sessionStorage.removeItem(
    ECONSENT_LOGIN_PROMPT_VERIFICATION_FILENAME_KEY,
  );
  window.dispatchEvent(
    new CustomEvent<EConsentStatusChangedDetail>(
      ECONSENT_STATUS_CHANGED_EVENT,
      {
        detail: { ...detail, submitted: true },
      },
    ),
  );
}

// Local instance: DOMPurify.addHook mutates whatever instance it's called on, and the
// shared default import is used by other sanitize() call sites in the app that must stay
// unaffected.
const externalLinkPurifier = DOMPurify(window);
externalLinkPurifier.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName !== 'A') return;

  const href = node.getAttribute('href');
  if (!href) return;

  const lowerHref = href.trim().toLowerCase();
  const isNativeAppScheme =
    lowerHref.startsWith('mailto:') || lowerHref.startsWith('tel:');
  if (isNativeAppScheme) return;

  let isInternal: boolean;
  try {
    const resolved = new URL(href, window.location.href);
    const currentOrigin = window.location.origin;
    // window.location.origin is the opaque string "null" under Capacitor's non-special
    // "capacitor:" scheme, and resolving a "//host" href against it also yields "null"
    // regardless of host — origin equality would misclassify any such href as internal there,
    // so hosts are compared instead in that case.
    isInternal =
      currentOrigin === 'null'
        ? resolved.host === new URL(window.location.href).host
        : resolved.origin === currentOrigin;
  } catch {
    isInternal = true;
  }

  if (isInternal) return;

  node.setAttribute('target', '_blank');
  node.setAttribute('rel', 'noopener noreferrer');
});

export default function EConsentModal({ open, onClose }: ModuleModalProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [verificationCode, setVerificationCode] = useState('');
  const [verificationFilename, setVerificationFilename] = useState<
    string | null
  >(null);
  const [downloadedVerificationFilename, setDownloadedVerificationFilename] =
    useState<string | null>(null);
  const [inlineError, setInlineError] = useState('');
  const [generalError, setGeneralError] = useState('');
  const [statusResolved, setStatusResolved] = useState(false);
  const [isDownloadingVerificationPdf, setIsDownloadingVerificationPdf] =
    useState(false);
  const defaultPromptWindowConfig = useMemo(
    () => resolveDefaultEConsentPromptWindowConfig(),
    [],
  );
  const [promptTitle, setPromptTitle] = useState(
    resolveEConsentPromptTitle('', defaultPromptWindowConfig.title),
  );
  const [promptDescriptionHtml, setPromptDescriptionHtml] = useState(
    defaultPromptWindowConfig.descriptionHtml,
  );
  const [confirmLabel, setConfirmLabel] = useState('');

  const userInfo = useSelector<WARootState, EConsentAppUserInfo | undefined>(
    (state) => state.appView.UserInfo,
  );
  const advisorGroupId = useMemo(
    () => deriveAdvisorGroupId(userInfo),
    [userInfo],
  );
  const token = userInfo?.Token;
  const currentRole = useSelector(
    (state: WARootState) => state.auth.currentRole,
  );

  const eConsentConfig = useMemo(
    () =>
      open && advisorGroupId !== null
        ? resolveRuntimeEConsentConfig(advisorGroupId, userInfo)
        : null,
    [advisorGroupId, open, userInfo],
  );

  const profileArg =
    open && token && hasUserProfile(currentRole) ? undefined : skipToken;
  const {
    data: profile,
    isLoading: isLoadingProfile,
    isFetching: isFetchingProfile,
  } = useGetUserProfileQuery(profileArg);

  const [getEConsentStatus, { isLoading: isLoadingStatus }] =
    useGetEConsentStatusMutation();
  const [markEConsentUserIgnored] = useMarkEConsentUserIgnoredMutation();
  const [submitEConsent, { isLoading: isSubmitting }] =
    useSubmitEConsentMutation();

  const sanitizedDescriptionHtml = useMemo(
    () =>
      externalLinkPurifier.sanitize(promptDescriptionHtml, {
        USE_PROFILES: { html: true },
      }),
    [promptDescriptionHtml],
  );

  const isBusy =
    isSubmitting || isLoadingStatus || isDownloadingVerificationPdf;

  const loadLatestConsentStatus = useCallback(
    async (request: EConsentStatusRequest): Promise<boolean> => {
      const response = await getEConsentStatus(request).unwrap();
      setPromptTitle(
        resolveEConsentPromptTitle(
          response.promptActionLabel,
          defaultPromptWindowConfig.title,
        ),
      );
      setPromptDescriptionHtml(
        hasVisibleHtmlContent(response.description)
          ? response.description
          : defaultPromptWindowConfig.descriptionHtml,
      );
      setConfirmLabel(response.confirmLabel.trim());

      if (response.submitted) {
        persistAnsweredPromptState({
          advisorGroupId: request.fi_id,
          userId: request.user_id,
        });
        setVerificationFilename(null);
        setStatusResolved(true);
        return true;
      }

      setVerificationFilename(response.verificationFilename);
      persistPendingPromptState(response.verificationFilename);
      setStatusResolved(true);
      return false;
    },
    [
      defaultPromptWindowConfig.descriptionHtml,
      defaultPromptWindowConfig.title,
      getEConsentStatus,
    ],
  );

  useEffect(() => {
    if (!open) {
      setVerificationCode('');
      setDownloadedVerificationFilename(null);
      setInlineError('');
      setGeneralError('');
      setStatusResolved(false);
      setPromptTitle(
        resolveEConsentPromptTitle('', defaultPromptWindowConfig.title),
      );
      setPromptDescriptionHtml(defaultPromptWindowConfig.descriptionHtml);
      setConfirmLabel('');
      return;
    }

    const cachedVerificationFilename = window.sessionStorage.getItem(
      ECONSENT_LOGIN_PROMPT_VERIFICATION_FILENAME_KEY,
    );

    if (cachedVerificationFilename) {
      setVerificationFilename(cachedVerificationFilename);
    }

    if (statusResolved || isLoadingProfile || isFetchingProfile) {
      return;
    }

    const request = buildConsentStatusRequest(
      userInfo,
      advisorGroupId,
      profile,
    );
    if (!request) {
      return;
    }

    void loadLatestConsentStatus(request)
      .then((submitted) => {
        if (!submitted) {
          return;
        }

        setGeneralError(
          t(
            'eConsent.modal.alreadySubmitted',
            'Your eConsent response has already been recorded.',
          ),
        );
      })
      .catch((error: unknown) => {
        setGeneralError(
          getErrorMessage(error) ||
            t(
              'eConsent.modal.errorLoadingPrompt',
              'Unable to load your eConsent request. Please try again.',
            ),
        );
      });
  }, [
    defaultPromptWindowConfig.descriptionHtml,
    defaultPromptWindowConfig.title,
    isFetchingProfile,
    isLoadingProfile,
    loadLatestConsentStatus,
    open,
    profile,
    statusResolved,
    t,
    advisorGroupId,
    userInfo,
  ]);

  const closeOnly = () => {
    setVerificationCode('');
    setInlineError('');
    setGeneralError('');
    onClose();
  };

  const handleSubmit = async (body: {
    accept: boolean;
    decline: boolean;
    validation_code?: string;
    verification_filename?: string;
  }) => {
    if (!userInfo?.UserId) {
      return;
    }

    setInlineError('');
    setGeneralError('');

    try {
      const response = await submitEConsent({
        userId: userInfo.UserId,
        body,
      }).unwrap();

      if (!response.success) {
        if (response.verificationFilename) {
          setVerificationFilename(response.verificationFilename);
          setDownloadedVerificationFilename(null);
          persistPendingPromptState(response.verificationFilename);
        }

        setInlineError(
          t(
            'eConsent.modal.invalidVerificationCode',
            "That code doesn't match. Please try again.",
          ),
        );
        return;
      }

      persistAnsweredPromptState({
        advisorGroupId: advisorGroupId ?? undefined,
        userId: userInfo.UserId,
      });
      closeOnly();
    } catch (error: unknown) {
      setGeneralError(
        getErrorMessage(error) ||
          t(
            'eConsent.modal.errorSubmittingElection',
            'Unable to submit your response. Please try again.',
          ),
      );
    }
  };

  const handleAccept = async () => {
    const trimmedCode = verificationCode.trim();
    if (!trimmedCode) {
      return;
    }

    if (!isNumericVerificationCode(trimmedCode)) {
      setInlineError(
        t(
          'eConsent.modal.numericCodeRequired',
          'Enter a numeric verification code.',
        ),
      );
      return;
    }

    if (!isFourDigitVerificationCode(trimmedCode)) {
      setInlineError(
        t(
          'eConsent.modal.fourDigitCodeRequired',
          'Enter the 4-digit verification code.',
        ),
      );
      return;
    }

    if (!downloadedVerificationFilename) {
      setInlineError(
        t(
          'eConsent.modal.downloadVerificationPdfRequired',
          'Download the PDF before entering your verification code.',
        ),
      );
      return;
    }

    if (!verifyPdfCode(trimmedCode, downloadedVerificationFilename)) {
      setInlineError(
        t(
          'eConsent.modal.invalidVerificationCode',
          "That code doesn't match. Please try again.",
        ),
      );
      return;
    }

    const verificationFilenameHash = extractHashFromPdfFilename(
      downloadedVerificationFilename,
    );
    if (!verificationFilenameHash) {
      setInlineError(
        t(
          'eConsent.modal.invalidVerificationPdf',
          'Download the PDF again before submitting your response.',
        ),
      );
      return;
    }

    await handleSubmit({
      accept: true,
      decline: false,
      validation_code: trimmedCode,
      verification_filename: verificationFilenameHash,
    });
  };

  const handleDecline = async () => {
    await handleSubmit({
      accept: false,
      decline: true,
    });
  };

  const handleCancelClose = () => {
    if (isBusy) {
      return;
    }

    if (eConsentConfig?.firstLoginOnly) {
      if (userInfo?.UserId) {
        void markEConsentUserIgnored({ userId: userInfo.UserId })
          .unwrap()
          .catch((error: unknown) => {
            console.warn('Unable to mark eConsent prompt user ignored', error);
          });
      }

      closeOnly();
      return;
    }

    closeOnly();
  };

  const handleDownloadVerificationPdf = async () => {
    if (isBusy) {
      return;
    }

    setInlineError('');
    setGeneralError('');
    setIsDownloadingVerificationPdf(true);

    try {
      const pdf = await fetchRandomVerificationPdf();

      if (!pdf) {
        setGeneralError(
          t(
            'eConsent.modal.errorDownloadingVerificationPdf',
            'Unable to download your verification PDF. Please try again.',
          ),
        );
        return;
      }

      await triggerBlobDownload(pdf.blob, pdf.filename, MimeType.Pdf);
      setDownloadedVerificationFilename(pdf.filename);
      setVerificationFilename(pdf.filename);
      persistPendingPromptState(pdf.filename);
    } catch (error: unknown) {
      setGeneralError(
        getErrorMessage(error) ||
          t(
            'eConsent.modal.errorDownloadingVerificationPdf',
            'Unable to download your verification PDF. Please try again.',
          ),
      );
    } finally {
      setIsDownloadingVerificationPdf(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancelClose}
      fullWidth
      maxWidth={false}
      slotProps={{
        paper: {
          sx: {
            width: isMobile ? 'calc(100vw - 24px)' : 900,
            maxWidth: 'calc(100vw - 24px)',
            maxHeight: '85vh',
            display: 'flex',
            flexDirection: 'column',
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pr: 1,
        }}
      >
        <Typography
          component='div'
          variant='h6'
        >
          {promptTitle}
        </Typography>
        <IconButton
          aria-label={t('close', 'Close')}
          onClick={handleCancelClose}
          disabled={isBusy}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          pb: 3,
        }}
      >
        {(isLoadingProfile || isFetchingProfile || isLoadingStatus) && (
          <Box
            display='flex'
            justifyContent='center'
            py={3}
          >
            <CircularProgress size={28} />
          </Box>
        )}

        {sanitizedDescriptionHtml && (
          <Box
            sx={{
              typography: 'body2',
              color: 'text.primary',
              maxHeight: isMobile ? '28vh' : '32vh',
              overflowY: 'auto',
              pr: 1,
            }}
            dangerouslySetInnerHTML={{ __html: sanitizedDescriptionHtml }}
          />
        )}

        {(statusResolved || verificationFilename) && !generalError && (
          <Button
            onClick={() => {
              void handleDownloadVerificationPdf();
            }}
            disabled={isBusy}
            variant='contained'
            sx={{ alignSelf: 'flex-start' }}
          >
            {isDownloadingVerificationPdf
              ? t(
                  'eConsent.modal.downloadingVerificationPdf',
                  'Downloading PDF...',
                )
              : t(
                  'eConsent.modal.downloadVerificationPdf',
                  'Download PDF to obtain your verification code',
                )}
          </Button>
        )}

        <TextField
          fullWidth
          value={verificationCode}
          onChange={(event) => {
            setVerificationCode(event.target.value);
            setInlineError('');
          }}
          placeholder={t(
            'eConsent.modal.verificationCodePlaceholder',
            'Enter your verification code',
          )}
          error={Boolean(inlineError)}
          helperText={inlineError || ' '}
          slotProps={{ htmlInput: { inputMode: 'numeric' } }}
        />

        {generalError && <Alert severity='error'>{generalError}</Alert>}
      </DialogContent>

      <DialogActions
        sx={{
          position: 'sticky',
          bottom: 0,
          backgroundColor: 'background.paper',
          borderTop: (dialogTheme) =>
            `1px solid ${dialogTheme.palette.divider}`,
          px: 3,
          py: 2,
          justifyContent: 'space-between',
        }}
      >
        <Button
          onClick={handleCancelClose}
          disabled={isBusy}
          variant='text'
          color='inherit'
        >
          {t('cancel', 'Cancel')}
        </Button>

        <Box
          display='flex'
          gap={1.5}
        >
          <Button
            onClick={() => {
              void handleDecline();
            }}
            disabled={isBusy}
            variant='outlined'
          >
            {t('eConsent.modal.decline', 'Decline')}
          </Button>
          <Button
            onClick={() => {
              void handleAccept();
            }}
            disabled={isBusy || !verificationCode.trim()}
            variant='contained'
          >
            {confirmLabel || t('eConsent.modal.accept', 'Accept')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
